import {
  Component,
  Input,
  ViewChild,
  ElementRef,
  AfterViewInit,
  Renderer2,
  Signal,
  effect,
  inject,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TopbarComponent } from './topbar/topbar';
import { FavbarComponent } from "./favbar/favbar";
import { SidebarComponent } from "./sidebar/sidebar";
import { HeaderComponent } from './header/header';
import { Router, RouterModule, RouterOutlet } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { GlobalSearchService, FeatureResult } from '@ui-nuvertos/shared';

type LayoutSubItem = {
  label: string;
  route: string;
  module_id: number;
  sub_module_id: number;
  module_key?: string;
};

type LayoutMenuItem = {
  key: string;
  label: string;
  icon: string;
  route?: string;
  module_id: number;
  module_key?: string;
  children?: LayoutSubItem[];
};

@Component({
  selector: 'lib-layout',
  standalone: true,
  imports: [TopbarComponent, FavbarComponent, SidebarComponent, RouterOutlet, RouterModule, ButtonModule, HeaderComponent],
  templateUrl: './layout.html',
  styleUrl: './layout.scss',
})

export class Layout implements AfterViewInit {
  @Input() favbarButtons: Array<{ label: string; route: string; module_id: number }> = [];
  @Input() sidebarItems: LayoutMenuItem[] = [];

  /** When provided, takes precedence over route-data `sidebarItems`. The
   *  signal is read into the existing `sidebarItems` array via an effect so
   *  the rest of this component (registerFeatures etc.) stays unchanged. */
  @Input() sidebarItemsSignal?: Signal<LayoutMenuItem[]>;

  @ViewChild('sidebarRef') sidebarRef!: SidebarComponent;

  private route = inject(ActivatedRoute);
  private renderer = inject(Renderer2);
  private el = inject(ElementRef);
  private globalSearchService = inject(GlobalSearchService);

  constructor(private router: Router) {
    const data = this.route.snapshot.data as {
      favbarButtons?: Array<{ label: string; route: string; module_id: number }>;
      sidebarItems?: LayoutMenuItem[];
      searchModuleOptions?: Array<{ label: string; prefix: string; icon: string; color: string }>;
      searchApiBaseUrl?: string;
      searchEntityTypes?: string[];
    };
    if (data) {
      this.favbarButtons = data.favbarButtons ?? this.favbarButtons;
      // Route-data sidebarItems used only when no signal Input provided.
      if (!this.sidebarItemsSignal) {
        this.sidebarItems = data.sidebarItems ?? this.sidebarItems;
      }

      // Register features for global search
      if (this.sidebarItems?.length) {
        this.registerFeatures(this.sidebarItems);
      }

      // Set app-specific search module options
      if (data.searchModuleOptions?.length) {
        this.globalSearchService.setModuleOptions(data.searchModuleOptions);
      }

      if (data.searchApiBaseUrl) {
        this.globalSearchService.setApiBaseUrl(data.searchApiBaseUrl);
      }

      if (data.searchEntityTypes?.length) {
        this.globalSearchService.setAllowedEntityTypes(data.searchEntityTypes);
      }
    }

    // Reactively re-sync when the signal changes (and on first run).
    effect(() => {
      if (!this.sidebarItemsSignal) return;
      const items = this.sidebarItemsSignal();
      this.sidebarItems = items ?? [];
      if (this.sidebarItems.length) this.registerFeatures(this.sidebarItems);
    });
  }

  private registerFeatures(items: any[]): void {
    const features: FeatureResult[] = [];

    const processItem = (item: any) => {
      // Register item if it has a route (is navigable)
      if (item.route) {
        features.push({
          label: item.label,
          route: item.route,
          icon: item.icon, // Keep original icon class/name
          description: item.key ? `Go to ${item.label}` : undefined
        });
      }

      // Recursively process children
      if (item.children && Array.isArray(item.children)) {
        item.children.forEach(processItem);
      }
    };

    items.forEach(processItem);
    this.globalSearchService.registerFeatures(features);
  }

  ngAfterViewInit() {
    // Set initial state
    this.updateSidebarWidth();
  }

  onSidebarToggled(isExpanded: boolean) {
    const sidebarWidth = isExpanded ? '13.8rem' : '3.5rem';
    this.renderer.setStyle(this.el.nativeElement, '--sidebar-w', sidebarWidth);

    // Also add/remove CSS class for additional styling if needed
    if (isExpanded) {
      this.renderer.removeClass(this.el.nativeElement, 'sidebar-collapsed');
    } else {
      this.renderer.addClass(this.el.nativeElement, 'sidebar-collapsed');
    }
  }

  private updateSidebarWidth() {
    const isExpanded = this.sidebarRef?.expanded?.() ?? true;
    this.onSidebarToggled(isExpanded);
  }
}
